<script>
import axios from 'axios'
import formatBytes from '@utils/format-bytes'
import AccAppDetail from './acc_app_detail'

// The tops collector runs hourly: polling faster than this only re-reads the
// same hour (the backend also caches each answer for 120 s).
const REFRESH_INTERVAL_MS = 300000
const RANGE_LABELS = { '1h': 'the latest hour', '24h': 'the last 24 hours', '7d': 'the last 7 days' }
const TOP_APPS_IN_CHART = 10
// PAN-OS application risk, 1 (lowest) to 5 (highest).
const RISK_VARIANTS = { 1: 'success', 2: 'success', 3: 'info', 4: 'warning', 5: 'danger' }
const RISK_COLORS = { 1: '#0acf97', 2: '#0acf97', 3: '#39afd1', 4: '#ffbc00', 5: '#fa5c7c' }
const HIGH_RISK = 4

export default {
  components: {
    AccAppDetail
  },
  props: {
    basePath: {
      type: String,
      default: '/monitoring/acc'
    }
  },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      summary: {},
      tops: {},
      site: '',
      range: '1h',
      rangeOptions: [
        { value: '1h', text: 'Last hour' },
        { value: '24h', text: '24 h' },
        { value: '7d', text: '7 d' }
      ],
      appMetric: 'sessions',
      metricOptions: [
        { value: 'sessions', text: 'Sessions' },
        { value: 'bytes', text: 'Bytes' }
      ],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortBy: 'sessions',
      sortDesc: true,
      filter: '',
      isBusy: false,
      loadError: '',
      lastUpdated: null,
      refreshTimer: null,
      selectedApp: '',
      fields: [
        { key: 'rank', label: '#', sortable: false },
        { key: 'app', label: 'Application', sortable: true },
        { key: 'risk', label: 'Risk', sortable: true },
        { key: 'sessions', label: 'Sessions', sortable: true },
        { key: 'bytes', label: 'Bytes', sortable: true },
        { key: 'share', label: 'Share', sortable: false },
        { key: 'sites', label: 'Sites', sortable: false }
      ]
    }
  },
  computed: {
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    collector () {
      return this.summary.collector || {}
    },
    collectorAgeText () {
      const seconds = this.collector.age_seconds
      if (typeof seconds !== 'number') return 'unknown'
      if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`
      return `${(seconds / 3600).toFixed(1)} h ago`
    },
    siteOptions () {
      const sites = (this.summary.site_options || []).map((site) => ({ value: site, text: site }))
      return [{ value: '', text: 'All sites' }, ...sites]
    },
    sites () {
      return this.summary.sites || []
    },
    rangeLabel () {
      return RANGE_LABELS[this.range] || RANGE_LABELS['1h']
    },
    isMultiHour () {
      return this.range !== '1h'
    },
    // The least-covered site sets the headline: totals of a site that missed
    // collector runs read low, and that is easy to mistake for less traffic.
    coverage () {
      const covered = this.sites.filter((item) => item.reporting_device)
      if (!covered.length) return null
      const worst = covered.reduce((low, item) =>
        item.hours_covered / item.hours_expected < low.hours_covered / low.hours_expected ? item : low)
      return {
        covered: worst.hours_covered,
        expected: worst.hours_expected,
        complete: covered.every((item) => item.hours_covered >= item.hours_expected),
        site: worst.site
      }
    },
    apps () {
      return (this.tops.apps || []).map((app, index) => ({ ...app, rank: index + 1 }))
    },
    categories () {
      return this.tops.categories || []
    },
    blockedUrls () {
      return this.tops.blocked_urls || []
    },
    urlCategoriesCollected () {
      return !this.tops.url_categories || this.tops.url_categories.collected
    },
    metricTotal () {
      return this.apps.reduce((total, app) => total + (app[this.appMetric] || 0), 0)
    },
    kpis () {
      const s = this.summary
      const allSites = (s.site_options || []).length
      // Over several hours an app counts only in the hours it made the top 25.
      const approx = this.isMultiHour ? ' Approximate: the sum of each hour\'s top-25 list.' : ''
      return [
        {
          label: 'Sessions',
          value: this.compact(s.sessions),
          icon: 'repeat',
          bg: 'bg-primary',
          help: `Sessions of the top-25 applications in ${this.rangeLabel}, summed over the sites in scope.${approx}`
        },
        {
          label: 'Traffic',
          value: this.bytes(s.bytes),
          icon: 'download-cloud',
          bg: 'bg-info',
          help: `Bytes of the top-25 applications in ${this.rangeLabel}, summed over the sites in scope.${approx}`
        },
        {
          label: 'Apps seen',
          value: s.apps_seen || 0,
          icon: 'grid',
          bg: 'bg-success',
          help: 'Distinct applications across the top-25 lists of the sites in scope.'
        },
        {
          label: 'High-risk apps',
          value: s.high_risk_apps || 0,
          icon: 'alert-triangle',
          bg: 'bg-warning',
          valueClass: (s.high_risk_apps || 0) > 0 ? 'text-warning' : '',
          help: `Applications with PAN-OS risk ${HIGH_RISK} or 5 among those seen.`
        },
        {
          label: 'Threats',
          value: this.compact(s.threats),
          icon: 'alert-octagon',
          bg: 'bg-danger',
          valueClass: (s.threats || 0) > 0 ? 'text-danger' : '',
          help: `Threat count reported by the application-categories summary in ${this.rangeLabel}.`
        },
        {
          label: 'Blocked URLs',
          value: s.blocked_urls || 0,
          suffix: allSites ? `${s.sites_with_blocked_urls || 0}/${(s.sites || []).length} sites` : '',
          icon: 'slash',
          bg: 'bg-secondary',
          help: `Distinct blocked URLs in ${this.rangeLabel}. A site with no URL-filtering logs reports none.`
        }
      ]
    },
    topAppsChart () {
      return this.apps.slice().sort((a, b) => (b[this.appMetric] || 0) - (a[this.appMetric] || 0))
        .slice(0, TOP_APPS_IN_CHART)
    },
    topAppsSeries () {
      return [{
        name: this.appMetric === 'bytes' ? 'Bytes' : 'Sessions',
        data: this.topAppsChart.map((app) => app[this.appMetric] || 0)
      }]
    },
    topAppsOptions () {
      const format = this.appMetric === 'bytes' ? this.bytes : this.compact
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '60%',
            borderRadius: 3,
            distributed: true,
            dataLabels: { position: 'top' }
          }
        },
        colors: this.topAppsChart.map((app) => RISK_COLORS[app.risk] || '#727cf5'),
        legend: { show: false },
        // Labels sit just past the end of each bar: inside, the long tail of
        // small apps would clip them ("16.", "9.").
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          offsetX: 6,
          style: { colors: ['#6c757d'], fontWeight: 400 },
          formatter: (value) => format(value)
        },
        xaxis: {
          categories: this.topAppsChart.map((app) => app.app),
          labels: { formatter: (value) => format(value) }
        },
        tooltip: { y: { formatter: (value) => format(value) } }
      }
    },
    categorySeries () {
      return this.categories.map((row) => row.bytes || 0)
    },
    categoryOptions () {
      return {
        chart: { type: 'donut' },
        labels: this.categories.map((row) => row.category),
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (value) => this.bytes(value) } }
      }
    },
    riskMix () {
      return this.tops.risk_mix || []
    },
    hasRiskMix () {
      return this.riskMix.some((row) => row.apps > 0)
    },
    riskSeries () {
      return [{
        name: this.appMetric === 'bytes' ? 'Bytes' : 'Sessions',
        data: this.riskMix.map((row) => row[this.appMetric] || 0)
      }]
    },
    riskOptions () {
      const format = this.appMetric === 'bytes' ? this.bytes : this.compact
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 3, distributed: true } },
        colors: this.riskMix.map((row) => RISK_COLORS[row.risk]),
        legend: { show: false },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.riskMix.map((row) => [`Risk ${row.risk}`, `${row.apps} app${row.apps === 1 ? '' : 's'}`])
        },
        yaxis: { labels: { formatter: (value) => format(value) } },
        tooltip: { y: { formatter: (value) => format(value) } }
      }
    }
  },
  watch: {
    site () {
      this.currentPage = 1
      this.loadAll()
    },
    range () {
      this.currentPage = 1
      this.loadAll()
    }
  },
  mounted () {
    this.loadAll()
    // Do not poll from background tabs (see sdwan.vue for rationale).
    this.refreshTimer = setInterval(() => {
      if (document.hidden) return
      this.loadAll()
    }, REFRESH_INTERVAL_MS)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    loadAll () {
      // Never stack a refresh cycle on top of one still in flight.
      if (this.isBusy) return
      this.isBusy = true
      this.loadError = ''
      const base = `${this.baseURL}${this.basePath}`
      const params = { range: this.range }
      if (this.site) params.site = this.site
      Promise.all([
        axios.get(`${base}/summary`, { params }),
        axios.get(`${base}/tops`, { params })
      ])
        .then(([summary, tops]) => {
          this.summary = this.parse(summary.data)
          this.tops = this.parse(tops.data)
          this.totalRows = this.apps.length
          this.lastUpdated = new Date()
        })
        .catch((error) => {
          this.loadError = `Could not load ACC data: ${this.errorDetail(error)}`
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    // A 400 carries {"error": "..."} (bad site/range); a 500 may be Chalice's
    // {"Message": "..."} or not JSON at all.
    errorDetail (error) {
      try {
        const body = this.parse((error.response && error.response.data) || '{}')
        return body.error || body.Message || error.message
      } catch (e) {
        return error.message
      }
    },
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    showDetails (item) {
      this.selectedApp = item.app
      this.$refs['modal-app'].show()
    },
    onDetailsShown () {
      // ApexCharts sizes itself against the width available when it renders;
      // inside a modal that width is final only once the dialog is shown
      // (see firewalls.vue).
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },
    bytes (value) {
      return formatBytes(value)
    },
    compact (value) {
      if (typeof value !== 'number') return '0'
      return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
    },
    share (app) {
      if (!this.metricTotal) return '—'
      return `${((app[this.appMetric] || 0) * 100 / this.metricTotal).toFixed(1)} %`
    },
    riskVariant (risk) {
      return RISK_VARIANTS[risk] || 'secondary'
    },
    stateVariant (state) {
      if (state === 'active' || state === 'standalone') return 'success'
      if (state === 'passive') return 'info'
      if (!state) return 'secondary'
      return 'danger'
    },
    hitsText (row) {
      return row.capped ? `${row.hits}+` : `${row.hits}`
    },
    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    }
  }
}
</script>

<template>
  <div class="acc-page">
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <b-alert
      :show="collector.status === 'down'"
      variant="warning"
      class="d-flex align-items-center py-1"
    >
      <feather
        type="alert-triangle"
        class="icon-xs mr-2"
      />
      <span>
        <b>Tops collector stale</b> — no new reports for {{ collectorAgeText.replace(' ago', '') }}
        (expected every hour). The data below is the last hour each site reported.
      </span>
    </b-alert>

    <!-- Toolbar -->
    <div class="d-flex align-items-center flex-wrap mb-2">
      <b-form-select
        v-model="site"
        size="sm"
        :options="siteOptions"
        :disabled="isBusy"
        class="mr-2"
        style="max-width: 220px;"
      />
      <b-form-radio-group
        v-model="range"
        :options="rangeOptions"
        :disabled="isBusy"
        buttons
        button-variant="outline-primary"
        size="sm"
        class="mr-2"
      />
      <b-badge
        v-if="isMultiHour && coverage"
        v-b-tooltip.hover
        :variant="coverage.complete ? 'success' : 'warning'"
        class="mr-2"
        :title="coverage.complete
          ? 'Every site has data for every hour of the range.'
          : `Hours with data for the least-covered site (${coverage.site}). The collector missed runs, so its totals read low. See Sites below.`"
      >
        {{ coverage.covered }}/{{ coverage.expected }} h covered
      </b-badge>
      <span
        v-if="isMultiHour"
        class="text-muted font-size-12 mr-2"
      >≈ sum of hourly top-25</span>
      <span
        v-if="lastUpdated"
        class="text-muted font-size-12"
      >
        Updated {{ formatDate(lastUpdated) }} (America/Bogota) · auto-refresh 5 min
      </span>
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-auto"
        :disabled="isBusy"
        @click="loadAll"
      >
        <feather
          type="refresh-cw"
          class="icon-xs mr-1"
        />
        Refresh
      </b-button>
    </div>

    <!-- KPI cards -->
    <div class="row">
      <div
        v-for="kpi in kpis"
        :key="`kpi-${kpi.label}`"
        class="col-xl-2 col-md-4 col-6 mb-4"
      >
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="text-muted font-size-13 mb-1">
                  {{ kpi.label }}
                  <feather
                    v-b-tooltip.hover
                    type="help-circle"
                    class="icon-xs help-tip"
                    :title="kpi.help"
                  />
                </h5>
                <h3
                  class="mb-0"
                  :class="kpi.valueClass"
                >
                  {{ kpi.value }}
                </h3>
                <span
                  v-if="kpi.suffix"
                  class="text-muted font-size-11"
                >{{ kpi.suffix }}</span>
              </div>
              <div
                class="avatar-sm rounded-circle align-self-center"
                :class="kpi.bg"
              >
                <span class="avatar-title bg-transparent">
                  <feather
                    :type="kpi.icon"
                    class="icon-sm"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="row">
      <div class="col-xl-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-1">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="bar-chart-2"
                  class="icon-xs mr-1 text-muted"
                />
                Top applications
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  :title="`The ten largest applications of ${rangeLabel}. Bar color is the PAN-OS risk: green 1-2, blue 3, yellow 4, red 5.`"
                />
              </h5>
              <b-form-radio-group
                v-model="appMetric"
                :options="metricOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                class="ml-auto"
              />
            </div>
            <apexchart
              v-if="topAppsChart.length"
              type="bar"
              height="300"
              :series="topAppsSeries"
              :options="topAppsOptions"
            />
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              <feather
                type="pie-chart"
                class="icon-xs mr-1 text-muted"
              />
              Application categories
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                :title="`Traffic of ${rangeLabel} by application category, in bytes.`"
              />
            </h5>
            <apexchart
              v-if="categorySeries.length"
              type="donut"
              height="280"
              :series="categorySeries"
              :options="categoryOptions"
            />
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-1">
              <feather
                type="shield"
                class="icon-xs mr-1 text-muted"
              />
              Risk mix
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                :title="`Applications of ${rangeLabel} grouped by PAN-OS risk (5 = highest), measured in the metric selected in Top applications. Under each bar: how many applications have that risk.`"
              />
            </h5>
            <apexchart
              v-if="hasRiskMix"
              type="bar"
              height="280"
              :series="riskSeries"
              :options="riskOptions"
            />
            <div
              v-else
              class="text-muted py-4 text-center"
            >
              No data yet.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Applications table + blocked URLs -->
    <div class="row">
      <div class="col-xl-8 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-2">
              <h5 class="font-size-14 mb-0">
                <feather
                  type="grid"
                  class="icon-xs mr-1 text-muted"
                />
                Applications
                <b-badge
                  variant="light"
                  class="ml-1"
                >
                  {{ totalRows }}
                </b-badge>
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="icon-xs help-tip"
                  :title="`Every application in the top-25 lists of ${rangeLabel}. Share is against the total of the metric selected in Top applications. Click a row for its hourly trend per site.`"
                />
              </h5>
              <b-form-input
                v-model="filter"
                size="sm"
                placeholder="Search application..."
                class="ml-auto"
                style="max-width: 240px;"
              />
            </div>

            <div class="table-responsive mb-0">
              <b-table
                :items="apps"
                :fields="fields"
                :busy="isBusy"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="['app']"
                responsive="sm"
                small
                show-empty
                hover
                :empty-text="`No applications reported in ${rangeLabel}.`"
                @filtered="onFiltered"
                @row-clicked="showDetails"
              >
                <template v-slot:table-busy>
                  <div class="text-center">
                    <br><br>
                    <b-spinner class="align-middle" />
                    <strong>&nbsp;&nbsp;Loading...</strong>
                  </div>
                </template>

                <template v-slot:cell(rank)="data">
                  <span class="text-muted">{{ data.item.rank }}</span>
                </template>

                <template v-slot:cell(app)="data">
                  <a
                    href="javascript:void(0)"
                    class="font-weight-bold"
                    title="Hourly sessions and bytes per site"
                    @click.stop="showDetails(data.item)"
                  >{{ data.item.app }}</a>
                </template>

                <template v-slot:cell(risk)="data">
                  <b-badge :variant="riskVariant(data.item.risk)">
                    {{ data.item.risk || '?' }}
                  </b-badge>
                </template>

                <template v-slot:cell(sessions)="data">
                  {{ (data.item.sessions || 0).toLocaleString('en-US') }}
                </template>

                <template v-slot:cell(bytes)="data">
                  {{ bytes(data.item.bytes) }}
                </template>

                <template v-slot:cell(share)="data">
                  <span class="text-muted">{{ share(data.item) }}</span>
                </template>

                <template v-slot:cell(sites)="data">
                  <span class="text-muted font-size-12">{{ (data.item.sites || []).join(', ') }}</span>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    />&nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4 mb-4">
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-2">
              <feather
                type="slash"
                class="icon-xs mr-1 text-danger"
              />
              Blocked URLs
              <b-badge
                variant="light"
                class="ml-1"
              >
                {{ blockedUrls.length }}
              </b-badge>
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                :title="`URLs blocked by URL filtering in ${rangeLabel}, with every URL category the firewall assigned. A + after the hits means at least one hour reached the firewall's report limit of 999, so the real count is higher.`"
              />
            </h5>
            <div
              v-if="blockedUrls.length"
              class="acc-scroll"
            >
              <div
                v-for="row in blockedUrls"
                :key="`url-${row.url}`"
                class="d-flex align-items-start border-bottom py-1 font-size-13"
              >
                <div class="flex-grow-1 text-truncate pr-2">
                  <div
                    class="font-weight-bold text-truncate"
                    :title="row.url"
                  >
                    {{ row.url }}
                  </div>
                  <b-badge
                    v-for="label in row.url_categories"
                    :key="`url-${row.url}-${label}`"
                    variant="light"
                    class="mr-1"
                  >
                    {{ label }}
                  </b-badge>
                </div>
                <div class="text-nowrap text-right">
                  <span :class="{ 'text-danger': row.capped }">{{ hitsText(row) }}</span>
                  <div class="text-muted font-size-11">
                    {{ (row.sites || []).join(', ') }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-muted py-3 text-center"
            >
              No blocked URLs reported in {{ rangeLabel }}.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sites -->
    <div class="row">
      <div class="col-12 mb-4">
        <div class="card mb-0">
          <div class="card-body py-2">
            <h5 class="font-size-14 mb-2">
              <feather
                type="map-pin"
                class="icon-xs mr-1 text-muted"
              />
              Sites
              <b-badge
                variant="light"
                class="ml-1"
              >
                {{ sites.length }}
              </b-badge>
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="icon-xs help-tip"
                title="One site per HA pair. Only the active member reports traffic, so the passive one always returns empty reports. Last data is the collector time; the device window is the firewall's own clock, which may not match Bogota time."
              />
            </h5>
            <div
              v-for="item in sites"
              :key="`site-${item.site}`"
              class="row align-items-center border-bottom py-1 mx-0 font-size-13 flex-nowrap acc-fixed-row"
            >
              <div
                class="px-0 font-weight-bold text-truncate"
                style="width: 200px; flex: none;"
                :title="item.site"
              >
                {{ item.site }}
              </div>
              <div
                class="px-2"
                style="width: 34%; flex: none;"
              >
                <span
                  v-for="member in item.members"
                  :key="`member-${member.device}`"
                  class="mr-2 text-nowrap"
                >
                  {{ member.device }}
                  <b-badge :variant="stateVariant(member.ha_state)">
                    {{ member.ha_state || 'unknown' }}
                  </b-badge>
                </span>
              </div>
              <div class="col px-2 text-muted text-truncate">
                <template v-if="item.reporting_device">
                  Data from {{ item.reporting_device }} · last {{ formatDate(item.last_data) }}
                  <feather
                    v-b-tooltip.hover
                    type="clock"
                    class="icon-xs help-tip"
                    :title="`Device window (firewall clock): ${item.device_window.start || '?'} → ${item.device_window.end || '?'}`"
                  />
                </template>
                <template v-else>
                  No member has reported traffic in the last 7 days.
                </template>
              </div>
              <div
                class="px-0 text-right text-nowrap"
                style="width: 90px; flex: none;"
              >
                <b-badge :variant="item.hours_covered >= item.hours_expected ? 'success' : 'warning'">
                  {{ item.hours_covered }}/{{ item.hours_expected }} h
                </b-badge>
              </div>
            </div>
            <div
              v-if="!sites.length"
              class="text-muted py-3 text-center"
            >
              No sites reported.
            </div>
            <div
              v-if="!urlCategoriesCollected"
              class="text-muted font-size-12 mt-2"
            >
              <feather
                type="info"
                class="icon-xs mr-1"
              />
              URL categories: not collected yet — the firewalls return no rows for this report.
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="acc-app-details"
      ref="modal-app"
      size="xl"
      :title="`Application: ${selectedApp}`"
      :centered="true"
      :no-fade="true"
      ok-only
      @shown="onDetailsShown"
    >
      <AccAppDetail
        :app="selectedApp"
        :base-url="baseURL"
        :base-path="basePath"
        :site="site"
        :range="range"
      />
    </b-modal>
  </div>
</template>

<style scoped>
.acc-scroll {
  max-height: min(420px, 50vh);
  overflow: auto;
}
/* Same fixed-column row as the Firewalls alerts: aligned on wide screens,
   reflowed into stacked lines under ~1100px instead of scrolling. */
.acc-fixed-row {
  min-width: 680px;
}
@media (max-width: 1100px) {
  .acc-fixed-row {
    min-width: 0;
    flex-wrap: wrap;
  }
  .acc-fixed-row > div {
    width: auto !important;
    flex: 0 0 auto !important;
    max-width: 100%;
    text-align: left !important;
  }
}
.help-tip {
  cursor: help;
  color: #ced4da;
}
.help-tip:hover {
  color: #98a6ad;
}
</style>

<style>
/* Unscoped on purpose: the svg inside vue-feather's root element does not
   carry the scoped attribute (see firewalls.vue). */
.acc-page .icon-xs {
  display: inline-flex;
  align-items: center;
  vertical-align: -0.15em;
  line-height: 1;
}
.acc-page .icon-xs svg {
  display: block;
  margin: 0;
}
.acc-page .help-tip svg {
  width: 12px !important;
  height: 12px !important;
}
</style>
